import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import CompanyGrid from "./Components/CompanyGrid/CompanyGrid";
import { Box } from "@mui/material";
import { AddOrEditCompany } from "./Components";
import CompanyContact from "./Components/CompanyContact/CompanyContact";
import CompanyOfficeGrid from "./Components/CompanyOfficeGrid/CompanyOfficeGrid";
import CompanyContactGrid from "./Components/CompanyContactGrid/CompanyContactGrid";
import CompanyOffices from "./Components/CompanyOffice/CompanyOffices";
import SlaReporting from "./Components/SlaReporting/SlaReporting";
import Permission from "src/Shared/Components/Permission/Permission";
import CompanyManagementLayout from "./Components/CompanyManagementLayout/CompanyManagementLayout";
import SlaManagement from "./Components/SLAManagement/SLAManagement";
import SalesAction from "./Components/SalesAction/SalesAction";
import SalesDeal from "./Components/SalesDeal/SalesDeal";
import CompanyIntegration from "./Components/CompanyIntegration/CompanyIntegration";
import CompanyActivity from "./Components/CompanyActivity/CompanyActivity";
import CompanyOverviewBox from "./Components/CompanyOverviewBox/CompanyOverviewBox";

import SLAComments from "./Components/SLAManagement/SLAComment/SLAComments";
import CompanyRevenue from "./Components/CompanyRevenue/CompanyRevenue";
import { observer } from "mobx-react";
import { useStore } from "src/Shared/Hooks";
import ProspectsGrid from "./Components/ProspectsGrid/ProspectsGrid";
import AddNewClient from "./Components/AddNewClient/AddNewClient";
import OnboardingWizard from "./Components/OnboardingWizard/OnboardingWizard";
import OnboardingSopOutput from "./Components/OnboardingWizard/OnboardingSopOutput";
import OnboardingSetupCopyView from "./Components/OnboardingWizard/OnboardingSetupCopyView";
import ClientDetails from "./Components/ClientDetails/ClientDetails";

const RedirectToOverview = () => {
  const { companyRowID } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const section = searchParams.get("section");
  if (section) {
    let targetPath = section;
    if (section === "contacts") {
      targetPath = "company-contacts";
    } else if (section === "offices") {
      targetPath = "company-offices";
    }
    searchParams.delete("section");
    const remainingSearch = searchParams.toString();
    const suffix = remainingSearch ? `?${remainingSearch}` : "";
    return <Navigate to={`/company/${companyRowID}/${targetPath}${suffix}`} replace />;
  }
  return <Navigate to={`/company/${companyRowID}/overview${location.search}`} replace />;
};

const RedirectToArchivedDetails = () => {
  const { companyRowID } = useParams();
  const location = useLocation();
  return <Navigate to={`/company/archived/${companyRowID}/details${location.search}`} replace />;
};

const CompanyModule = () => {
  const {
    rootStore: {
      companyStore: { companyOverview },
    },
  } = useStore();

  return (
    <>
      <Box>
        <Routes>
          <Route path="*" element={<CompanyGrid />}></Route>
          <Route path="prospects" element={<ProspectsGrid />} />

          {/* <Route path=":companyRowID?/:mode" element={<AddOrEditCompany />} /> */}
          {/* <Route path=":companyRowID/overview" element={<CompanyOverview />} /> */}
          <Route
            path=":companyRowID/addContacts"
            element={<CompanyContact />}
          />
          <Route path=":companyRowID/offices" element={<CompanyOfficeGrid />} />
          <Route
            path=":companyRowID/contacts"
            element={<CompanyContactGrid />}
          />
          <Route path=":companyRowID/addOffices" element={<CompanyOffices />} />
          <Route
            path="sla-reporting"
            element={
              <Permission permission="CRM.SlaReporting" redirect>
                <SlaReporting />
              </Permission>
            }
          />

          {/* Add Company — the three-route creation flow (P.5) */}
          <Route path="add" element={<AddNewClient />} />

          {/* Onboarding wizard (frame; capture steps land with the wizard features) */}
          <Route path=":companyRowID/onboarding" element={<OnboardingWizard />} />
          <Route
            path=":companyRowID/onboarding/sop"
            element={<OnboardingSopOutput />}
          />
          {/* The per-field setup view the Imperans team pastes from (S7.5). */}
          <Route
            path=":companyRowID/onboarding/setup-copy"
            element={<OnboardingSetupCopyView />}
          />

          {/* Archived */}

          <Route
            path="archived/:companyRowID"
            element={<CompanyManagementLayout />}
          >
            <Route index element={<RedirectToArchivedDetails />} />

            <Route
              path="details"
              element={<AddOrEditCompany subMenuRender />}
            />
          </Route>

          {/* Edit Company */}
          <Route path=":companyRowID" element={<CompanyManagementLayout />}>
            <Route index element={<RedirectToOverview />} />
            <Route path="overview" element={<CompanyOverviewBox />} />
            <Route path="sla" element={<SlaManagement />} />
            <Route path="actions" element={<SalesAction title="Actions" />} />
            <Route
              path="sale-deals"
              element={<SalesDeal title="Sale Deals" />}
            />
            <Route
              path="company-contacts"
              element={<CompanyContactGrid subMenuRender />}
            />
            <Route
              path="company-offices"
              element={<CompanyOfficeGrid subMenuRender />}
            />
            <Route
              path="comments"
              element={<SLAComments isSlaRelated={false} priceReview={false} />}
            />
            <Route path="details" element={<ClientDetails />} />
            <Route path="addOffices" element={<CompanyOffices />} />
            <Route path="imperans-link" element={<CompanyIntegration />} />
            <Route path="activity" element={<CompanyActivity />} />
            {companyOverview && companyOverview.companyIntegrationDetail != null && (
              <Route
                path="revenue"
                element={
                  <Permission permission="CRM.EstimatedAnnualRevenue" redirect>
                    <CompanyRevenue title="Estimated Annual Revenue" />
                  </Permission>
                }
              />
            )}
            <Route path="*" element={<Navigate to="overview" replace />} />
          </Route>
        </Routes>
      </Box>
    </>
  );
};

export default observer(CompanyModule);
